/*
  C How It Works — mobile pin.
  Below 978px the section's content sticks under the site header and the page scroll walks the
  steps track 1 → 2 → 3, resting on each whole step; once step 3 has been shown the page scrolls on.
  The scroll distance is a spacer (.c-hiw::after, --_pin-extra) of ~40svh per step change.
  The pinned block is centred in the viewport (never above the site header's height), so the
  blue left over is split above and below instead of piling up under the CTA, and a header that
  slides back in on scroll-up (the theme's sticky header) never covers the heading.
  With prefers-reduced-motion (or on desktop) there is no pin: the track is a plain snap rail.
*/
(() => {
  const MOBILE = window.matchMedia('(width < 978px)');
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  const PER_STEP = 0.4; // viewport heights of scroll per step change
  const HOLD = 0.3; // share of each segment spent resting on a step at either end

  const smooth = (x) => x * x * (3 - 2 * x);

  const init = (section) => {
    if (section.dataset.hiwReady) return;
    section.dataset.hiwReady = 'true';

    const content = section.querySelector('.c-hiw__content');
    const track = section.querySelector('[data-hiw-track]');
    if (!content || !track) return;
    const list = track.querySelector('.c-hiw__steps');
    const steps = [...track.querySelectorAll('.c-hiw__step')];
    let ticking = false;
    let targets = [];
    let extra = 0;

    // c-header when it's in use, else the theme's own header section (hides on scroll down,
    // slides back on scroll up — reserve its height either way)
    const headerHeight = () => {
      const header = document.querySelector('.c-header-wrapper, [data-c-header]') || document.querySelector('#shopify-section-header, .section-header');
      return header ? Math.round(header.getBoundingClientRect().height) : 0;
    };

    const measure = () => {
      const pinned = MOBILE.matches && !REDUCED.matches && steps.length > 1;
      section.classList.toggle('is-pinned', pinned);
      if (!pinned) {
        section.style.removeProperty('--_pin-extra');
        section.style.removeProperty('--_pin-top');
        list.style.removeProperty('--_shift');
        return false;
      }
      extra = Math.round(window.innerHeight * PER_STEP * (steps.length - 1));
      section.style.setProperty('--_pin-extra', `${extra}px`);
      const centred = Math.round((window.innerHeight - content.offsetHeight) / 2);
      section.style.setProperty('--_pin-top', `${Math.max(headerHeight(), centred)}px`);
      // Shift that brings each step to the track's start, clamped so the last one lands fully in view
      const pad = parseFloat(getComputedStyle(track).paddingLeft) || 0;
      const max = Math.max(0, list.scrollWidth + pad * 2 - track.clientWidth);
      targets = steps.map((step) => Math.min(max, Math.max(0, step.offsetLeft)));
      return true;
    };

    const update = () => {
      ticking = false;
      if (!section.classList.contains('is-pinned') || !extra) return;
      // The content starts sticking once its natural top (section top + padding) reaches the pin
      // line; the scroll since then, over the spacer height, is the progress.
      const start = section.getBoundingClientRect().top + parseFloat(getComputedStyle(section).paddingTop);
      const pinTop = parseFloat(section.style.getPropertyValue('--_pin-top')) || 0;
      const progress = Math.min(1, Math.max(0, (pinTop - start) / extra));
      const pos = progress * (steps.length - 1);
      const i = Math.min(steps.length - 2, Math.floor(pos));
      const frac = pos - i;
      const eased = smooth(Math.min(1, Math.max(0, (frac - HOLD) / (1 - 2 * HOLD))));
      const shift = targets[i] + (targets[i + 1] - targets[i]) * eased;
      list.style.setProperty('--_shift', `${shift.toFixed(1)}px`);
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    const refresh = () => {
      if (measure()) update();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', refresh, { passive: true });
    MOBILE.addEventListener('change', refresh);
    REDUCED.addEventListener('change', refresh);
    window.addEventListener('load', refresh);
    refresh();
  };

  const boot = (root = document) =>
    root.querySelectorAll('.c-hiw').forEach((section) => {
      if (section.querySelector('[data-hiw-track]')) init(section);
    });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
